import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { IconChartBar, IconRocket, IconSearch } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { Container, Heading, Text } from '../atomic';

/**
 * @typedef {Object} Step
 * @property {number} number - Step number
 * @property {string} title - Step title
 * @property {string} description - Step description
 * @property {string} [tag] - Short label shown under the description
 * @property {React.ReactNode} icon - Step icon
 */

/**
 * @typedef {Object} HowItWorksProps
 * @property {string} [sectionId] - Section ID for navigation
 * @property {string} [headline] - Section headline
 * @property {string} [subheadline] - Section subheadline
 */

/**
 * How It Works Section with 3-step process
 * Inspired by Lucky Orange + Stripe's storytelling
 *
 * @param {HowItWorksProps} props - Component props
 * @returns {JSX.Element} How it works section
 */
function HowItWorks({
  sectionId = 'how-it-works',
  headline = 'You define success. We build and deliver it.',
  subheadline = 'A discovery-driven model that takes you from data to dashboards in under a week.',
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /** @type {Step[]} */
  const steps = [
    {
      number: 1,
      title: 'Discover',
      description:
        'We learn how your organization reports financial performance and what outcomes matter to leadership.',
      tag: 'You define the outcome',
      icon: <IconSearch size={36} stroke={1.5} color="#ffffff" />,
    },
    {
      number: 2,
      title: 'Deploy',
      description:
        'We build your dashboards and reporting on AWS, enriching your cost data with the financial sources you already use.',
      tag: 'We do the work',
      icon: <IconRocket size={36} stroke={1.5} color="#ffffff" />,
    },
    {
      number: 3,
      title: 'Visualize',
      description:
        'The executive-ready reporting is delivered in EllaFin and accessed by the executives who need it, refreshed and managed by RSMC.',
      tag: 'Data to dashboards under a week',
      icon: <IconChartBar size={36} stroke={1.5} color="#ffffff" />,
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-advance steps for demonstration
  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % steps.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  /**
   * Render a single step
   * @param {Step} step - Step data
   * @param {number} index - Step index
   * @returns {JSX.Element} Step component
   */
  const renderStep = (step, index) => {
    const isActive = activeStep === index;
    const isPast = index < activeStep;

    return (
      <Box
        key={index}
        onClick={() => setActiveStep(index)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          position: 'relative',
          cursor: 'pointer',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.6s ease',
          transitionDelay: `${index * 200}ms`,
        }}
      >
        {/* Connector Line (not on last step) */}
        {index < steps.length - 1 && !isMobile && (
          <Box
            sx={{
              position: 'absolute',
              top: '44px',
              left: '50%',
              width: '100%',
              height: '3px',
              backgroundColor:
                isPast || isActive ? theme.palette.secondary.main : '#e0e0e0',
              transition: 'background-color 0.3s ease',
              zIndex: 0,
            }}
          />
        )}

        {/* Step Number Circle */}
        <Box
          sx={{
            width: '88px',
            height: '88px',
            borderRadius: '50%',
            backgroundColor: isActive
              ? theme.palette.secondary.main
              : isPast
                ? '#5e7b08'
                : '#e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.3s ease',
            boxShadow: isActive
              ? `0 8px 24px ${alpha(theme.palette.secondary.main, 0.4)}, 0 0 0 4px ${alpha(theme.palette.primary.light, 0.2)}`
              : '0 4px 12px rgba(0, 0, 0, 0.1)',
            transform: isActive ? 'scale(1.1)' : 'scale(1)',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: `0 8px 24px ${alpha(theme.palette.secondary.main, 0.4)}, 0 0 0 4px ${alpha(theme.palette.primary.light, 0.2)}`,
            },
          }}
        >
          {step.icon}
        </Box>

        {/* Step Number Badge */}
        <Box
          sx={{
            position: 'absolute',
            top: '-8px',
            left: '50%',
            transform: 'translateX(20px)',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.875rem',
            fontWeight: 700,
            zIndex: 2,
            border: '2px solid #ffffff',
          }}
        >
          {step.number}
        </Box>

        {/* Step Content */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: theme.palette.primary.main,
            mb: 1.5,
            fontSize: { xs: '1.25rem', md: '1.4rem' },
          }}
        >
          {step.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#6c757d',
            lineHeight: 1.7,
            maxWidth: '280px',
            fontSize: '0.95rem',
          }}
        >
          {step.description}
        </Typography>
        {step.tag && (
          <Box
            component="span"
            sx={{
              mt: 2,
              px: 1.5,
              py: 0.5,
              borderRadius: '999px',
              backgroundColor: alpha(theme.palette.secondary.main, 0.12),
              color: '#5e7b08',
              fontSize: '0.8rem',
              fontWeight: 600,
            }}
          >
            {step.tag}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      component="section"
      id={sectionId}
      ref={sectionRef}
      aria-label="How it works"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Heading
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          >
            {headline}
          </Heading>
          <Text
            sx={{
              fontSize: { xs: '1rem', md: '1.2rem' },
              color: '#6c757d',
              maxWidth: '500px',
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            {subheadline}
          </Text>
        </Box>

        {/* Steps */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 6, md: 4 },
            justifyContent: 'center',
            alignItems: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 4 },
          }}
        >
          {steps.map((step, index) => renderStep(step, index))}
        </Box>

        {/* Progress Indicators (Mobile) */}
        {isMobile && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              mt: 4,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                onClick={() => setActiveStep(index)}
                sx={{
                  width: activeStep === index ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor:
                    activeStep === index
                      ? theme.palette.secondary.main
                      : '#e0e0e0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                }}
              />
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default HowItWorks;
